/**
 * reborn.data.ts — Content for the "Reborn" chapter (CX-2726).
 *
 * Mechanical recycling, recycled-content grade levels, and the color
 * constraints that come with them. Ported from the demo-v784 reference; static
 * today, CMS-driven later (same `ChapterContent` shape — see cmfGuideData.ts).
 */

import type { ChapterContent } from "../chapter.types";

const REBORN_HERO_IMAGE =
  "https://mediahub.covestro.com/transform/f6c7356f-0567-4d9d-96dd-23a9ce8f9f48/90-PCR-Post-Consumer-Recycled-Polycarbonate";

const REBORN_CYAN_IMAGE =
  "https://mediahub.covestro.com/transform/5baab6cb-ebaf-4dba-a3d6-7adcd1f92333/Reborn-Cyan-CMF-Design";

export const rebornChapter: ChapterContent = {
  slug: "reborn",
  title: "Reborn",
  eyebrow: "Sustainability Guide",
  heroImage: REBORN_HERO_IMAGE,
  tags: ["PCR", "Mechanical Recycling", "Color Constraints", "CQ"],
  intro:
    "Mechanical recycling is the most common way to give plastic a second life. Plastic waste is collected, sorted, ground down, melted and re-pelletised — then blended with virgin material to produce grades with defined recycled content levels. The number after the R in the grade name tells you exactly how much: R30 means 30% post-consumer recycled content. R50 means 50%. R90 means 90%. The feedstock quality determines what you can do with color: higher recycled content means fewer color options, but a smaller carbon footprint.",

  gradeSectionTitle: "Color Applicability by Grade",
  suffixDecoder: {
    title: "Reading the grade name",
    text: "The number after the R tells you the recycled content level. R20 = 20% PCR. R30 = 30% PCR. R50 = 50% PCR. R65 = 65% PCR. R75 = 75% PCR. R90 = 90% PCR. The suffix CQ means the grade carries the Circular Intelligence designation — it contains at least 25% alternative, non-fossil raw materials and meets Covestro's threshold for a claimable environmental benefit. RE and RP suffixes indicate bio-circular and chemically recycled feedstocks respectively — see Chapter 2 for those.",
  },
  gradeTables: [
    {
      title: "Bayblend® (PC/ABS Blends)",
      columns: [
        "Grade",
        "PCR %",
        "Black",
        "Dark",
        "Light",
        "Chromatic",
        "CO₂ vs. virgin",
      ],
      rows: [
        ["Bayblend® virgin", "—", "✅", "✅", "✅", "✅", "—"],
        ["Bayblend® FR3017 R30", "30%", "✅", "✅", "Good†", "Good†", "−21%"],
        ["Bayblend® FR3040 R35", "35%", "✅", "✅", "Good†", "Good†", "−24%"],
        ["Bayblend® E85 R50 CQ", "50%", "✅", "✅", "Good†", "Limited", "−36%"],
        [
          "Bayblend® FR3008 R65",
          "65%",
          "✅",
          "✅",
          "Limited",
          "Limited",
          "−43%",
        ],
        [
          "Bayblend® FR3010 R75",
          "75%",
          "✅",
          "✅",
          "Limited",
          "Limited",
          "−48%",
        ],
      ],
    },
    {
      title: "Makrolon® (PC)",
      columns: [
        "Grade",
        "PCR %",
        "Black",
        "Dark",
        "Light",
        "Chromatic",
        "Notes",
      ],
      rows: [
        [
          "Makrolon® virgin",
          "—",
          "✅",
          "✅",
          "✅",
          "✅",
          "Full range incl. transparent",
        ],
        [
          "Makrolon® FR6007 R20",
          "20%",
          "✅",
          "✅",
          "✅",
          "✅ incl. white",
          "Best for white and light colors",
        ],
        [
          "Makrolon® 205 R50",
          "50%",
          "✅",
          "✅",
          "Good†",
          "Good†",
          "Transparent, medium viscosity",
        ],
        [
          "Makrolon® 705 R50",
          "50%",
          "✅",
          "✅",
          "Limited",
          "Limited",
          "Transparent, limited colorability",
        ],
        [
          "Makrolon® FR6017 R90 CQ",
          "90%",
          "✅",
          "✅",
          "Opaque only",
          "Opaque only",
          "−70% CO₂; UV stabilized, impact modified, FR",
        ],
      ],
    },
  ],

  notes: [
    {
      // Figma node 1657-9465 styles this processing caution with the blue
      // (info) callout treatment — light-blue #f5f8fa surface + blue left
      // border — while keeping the ⚠️ glyph. Not the orange warning tone.
      tone: "info",
      icon: "⚠️",
      text: "Recycled grades require adjusted processing parameters — particularly melt temperature and injection speed. Elevated melt temperature can cause surface degradation. For Makrolon RE CQ grades, barrel temperature at nozzle runs 270–305°C with mold temperature 70–120°C. Contact a Covestro expert before specifying any PCR grade for a new tool.",
    },
  ],

  story: {
    title: "Reborn Cyan — When Origin Becomes Aesthetic",
    image: REBORN_CYAN_IMAGE,
    paragraphs: [
      "Nongfu Spring is one of China's largest bottled water brands. Their polycarbonate water barrels are used in water coolers across the country — and when they reach end of life, they become the feedstock for something new.",
      "In a collaboration with Covestro, these barrels are recycled without adding colorants or aesthetic additives. The result is a material that carries the inherent tinted color of its origin — a faint, clean cyan that comes directly from the recycled feedstock itself. We call it Xin Sheng Dai — Reborn Cyan.",
      "This is a deliberate choice. Rather than hiding the recycled origin of the material, Reborn Cyan makes it visible. The purity and clarity of the feedstock means it performs as a high-quality, reliable alternative to virgin polycarbonate — with a lower carbon footprint and minimal additives that make it easier to recycle again.",
      "For designers, this opens a different kind of conversation with consumers. The color is not applied — it is inherited. That's a story worth telling.",
    ],
    callout: {
      tone: "info",
      icon: "💡",
      text: "Reborn Cyan is suitable for applications requiring transparency or translucency for decorative purposes — automotive interiors, consumer electronics, appliances, construction.",
    },
  },

  materialGroup: {
    primary: [
      {
        name: "Makrolon® R30 CQ",
        description: "Transparent feedstock, good colorability",
        link: { variant: "view-in-imagio", imagioUrl: "#" },
      },
      {
        name: "Makrolon® R50 CQ",
        description: "Transparent feedstock, limited chromatic",
        link: { variant: "view-in-imagio", imagioUrl: "#" },
      },
      {
        name: "Bayblend® R30 CQ",
        description: "PC/ABS, good colorability",
        link: { variant: "view-in-imagio", imagioUrl: "#" },
      },
    ],
    secondary: [
      {
        name: "Bayblend® R50 CQ",
        description: "PC/ABS, limited chromatic",
        link: { variant: "view-in-imagio", imagioUrl: "#" },
      },
      {
        name: "Bayblend® R75 CQ",
        description: "PC/ABS, high PCR, limited colors",
        link: { variant: "view-in-imagio", imagioUrl: "#" },
      },
    ],
  },

  // Material Samples — Figma card style (product render + spec + "Check it now").
  // TODO(CX-2726): the Figma design shows a product-render image and a full
  // Color / Material / Opacity / Surface Texture spec per sample, but the demo
  // reference only had names + short descriptions (spec values were placeholders
  // like "RAL 250 30 15"). Populate `productImage`, `ralColor`, `opacity`, and
  // `surfaceTexture` from real color/sample data (or the CMS) when available;
  // until then we render the short description and the "Check it now" CTA.
  samples: [
    {
      name: "Bayblend® FR3017 R30",
      description: "PC/ABS, 30% PCR, good colorability",
      link: { variant: "check-it-now" },
    },
    {
      name: "Bayblend® FR3040 R35",
      description: "PC/ABS, 35% PCR, thin wall FR",
      link: { variant: "check-it-now" },
    },
    {
      name: "Bayblend® E85 R50 CQ",
      description: "PC/ABS, 50% PCR, Circular Intelligence",
      link: { variant: "check-it-now" },
    },
    {
      name: "Bayblend® FR3008 R65",
      description: "PC/ABS, 65% PCR, limited colors",
      link: { variant: "check-it-now" },
    },
    {
      name: "Bayblend® FR3010 R75",
      description: "PC/ABS, 75% PCR, limited colors",
      link: { variant: "check-it-now" },
    },
    {
      name: "Makrolon® FR6007 R20",
      description: "PC, 20% PCR, best for white and light",
      link: { variant: "check-it-now" },
    },
    {
      name: "Makrolon® 205 R50",
      description: "PC, 50% PCR, transparent, medium viscosity",
      link: { variant: "check-it-now" },
    },
    {
      name: "Makrolon® 705 R50",
      description: "PC, 50% PCR, transparent",
      link: { variant: "check-it-now" },
    },
    {
      name: "Makrolon® FR6017 R90 CQ",
      description: "PC, 90% PCR, −70% CO₂, FR",
      link: { variant: "check-it-now" },
    },
  ],
};
